import React from 'react';

import { useIsDesktop } from '../../hooks/useIsDesktop';
import {
  ScoreSideToggles,
  type ScoreSideTogglesProps,
} from './ScoreSideToggles';
import {
  ProjectActionButtons,
  type ProjectActionButtonsProps,
} from './ProjectActionButtons';
import {
  ProjectEditActions,
  type ProjectEditActionsProps,
} from './ProjectEditActions';
import { ProjectPlaybackActions } from './ProjectPlaybackActions';
import type { ProjectToolbarMode } from './ProjectToolbar';

export type ProjectMobileBottomBarProps = Omit<
  ScoreSideTogglesProps,
  'orientation'
> &
  Omit<ProjectActionButtonsProps, 'popoverPlacement' | 'className'> &
  Omit<ProjectEditActionsProps, 'className'> & {
    /** `edit` swaps the toggles + actions row for the Reset / Undo /
     *  Done controls. `playback` swaps them for Play / Pause. */
    mode: ProjectToolbarMode;
    isPlaying?: boolean;
    canPlay?: boolean;
    playbackLoading?: boolean;
    playbackStatus?: string | null;
    onTogglePlay?: () => void;
  };

/**
 * Fixed bottom action bar shown only below `md`. On small screens the
 * global navbar is hidden and the top toolbar keeps just the Back +
 * title; the score toggles (left) and the Font Size / Download /
 * Feedback / Help
 * actions (right) live here instead. Popovers open upward so they stay
 * on-screen above the bar.
 *
 * The score content pages add bottom padding below `md` so the last
 * system isn't hidden behind this bar.
 */
export const ProjectMobileBottomBar: React.FC<ProjectMobileBottomBarProps> = ({
  mode,
  // Toggle props
  showAccidental,
  showAltFingerings,
  showPitch,
  pitchScope,
  showFingerings,
  hasNoteheadData,
  onToggleAccidental,
  onToggleAlt,
  onTogglePitch,
  onSelectPitchMode,
  onToggleFingerings,
  onTogglePencil,
  isEditMode,
  editDisabled,
  showPlayback,
  isPlaybackMode,
  playbackDisabled,
  // Edit-session props
  onReset,
  canReset,
  onUndo,
  canUndo,
  onDone,
  isSaving,
  isPlaying = false,
  canPlay = false,
  playbackLoading = false,
  playbackStatus = null,
  onTogglePlay,
  onTogglePlayback,
  // Action props (font size / download / help)
  ...actionProps
}) => {
  // Mount only below `md`: the desktop toolbar owns these controls there, and
  // rendering both would duplicate the font/download popovers + their listeners.
  const isDesktop = useIsDesktop();
  if (isDesktop) return null;

  return (
    <div
      // Tagged so the score pages can measure this bar's height and pad
      // the page bottom by exactly that much, instead of guessing. See
      // `useStickyAboveFooter`.
      data-mobile-bottom-bar
      className="fixed inset-x-0 bottom-0 z-40 bg-white md:hidden"
    >
      <div
        className={`
          flex flex-wrap items-center justify-between gap-x-1 gap-y-1 border-t
          border-black/20 px-2 py-1
        `}
      >
        {mode === 'edit' ? (
          // Edit mode dedicates the bar to Reset / Undo / Done — the same
          // controls the desktop top toolbar shows. Exit via Done; the
          // toggles + actions return once edit mode is off.
          <ProjectEditActions
            className="flex-1"
            onReset={onReset}
            canReset={canReset}
            onUndo={onUndo}
            canUndo={canUndo}
            onDone={onDone}
            isSaving={isSaving}
          />
        ) : mode === 'playback' && onTogglePlay ? (
          // The side pill that toggles playback lives in this bar on
          // small screens, so Exit has to sit here once the bar is
          // replaced. Play and Pause stay the only audio controls.
          <ProjectPlaybackActions
            isPlaying={isPlaying}
            canPlay={canPlay}
            isLoading={playbackLoading}
            statusText={playbackStatus}
            onTogglePlay={onTogglePlay}
            onExit={onTogglePlayback}
          />
        ) : (
          <>
            <ScoreSideToggles
              orientation="horizontal"
              showAccidental={showAccidental}
              showAltFingerings={showAltFingerings}
              showPitch={showPitch}
              pitchScope={pitchScope}
              showFingerings={showFingerings}
              hasNoteheadData={hasNoteheadData}
              onToggleAccidental={onToggleAccidental}
              onToggleAlt={onToggleAlt}
              onTogglePitch={onTogglePitch}
              onSelectPitchMode={onSelectPitchMode}
              onToggleFingerings={onToggleFingerings}
              onTogglePencil={onTogglePencil}
              isEditMode={isEditMode}
              editDisabled={editDisabled}
              showPlayback={showPlayback}
              isPlaybackMode={isPlaybackMode}
              onTogglePlayback={onTogglePlayback}
              playbackDisabled={playbackDisabled}
            />
            <ProjectActionButtons
              {...actionProps}
              popoverPlacement="top"
              className="ml-auto"
            />
          </>
        )}
      </div>
    </div>
  );
};
